import { useCallback, useContext } from "react";
import { useDispatch } from "react-redux";
import { useSession } from "@clerk/clerk-react";
import useActivityStore from "@/store/zustand/activityStore";
import { useCanvasId } from "@/utils/utils";
import { MessageRestoreStateContext } from "../contexts/MessageRestoreStateContext";

/**
 * Custom hook for message restoration functionality
 *
 * Encapsulates all the logic for restoring a message to a previous state,
 * including checking permissions, loading states, and activity lookups.
 *
 * @returns Object with restore function and state information
 */
export const useRestoreMessage = () => {
  const canvasId = useCanvasId();
  const dispatch = useDispatch();
  const { session } = useSession();

  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList,
  );
  const restoreActivity = useActivityStore((state) => state.restoreActivity);
  const restoringActivityId = useActivityStore(
    (state) => state.restoringActivityId,
  );

  const context = useContext(MessageRestoreStateContext);
  if (!context) {
    throw new Error(
      "useRestoreMessage must be used within MessageRestoreStateProvider",
    );
  }
  const { getRestoreState } = context;

  /**
   * Attempts to restore a message to its associated activity state
   *
   * @param messageId - The ID of the message to restore
   * @returns Promise that resolves when restore completes or is rejected
   */
  const restore = useCallback(
    async (messageId: string): Promise<boolean> => {
      // Get restore state for this message
      const { latestActivity, isOverwritten } = getRestoreState(messageId);

      // Validation checks
      if (!session) {
        console.warn("Cannot restore: No active session");
        return false;
      }

      if (!latestActivity) {
        console.warn("Cannot restore: No activity found for message");
        return false;
      }

      if (isOverwritten) {
        console.warn("Cannot restore: Activity has been overwritten");
        return false;
      }

      if (restoringActivityId) {
        console.warn("Cannot restore: Another restore is in progress");
        return false;
      }

      // Execute restore
      await restoreActivity(
        dispatch,
        canvasId,
        latestActivity.id,
        activityStreamList,
        session,
      );

      return true;
    },
    [
      session,
      getRestoreState,
      restoringActivityId,
      restoreActivity,
      dispatch,
      canvasId,
      activityStreamList,
    ],
  );

  /**
   * Gets the restore state for a specific message
   */
  const getMessageRestoreInfo = useCallback(
    (messageId: string) => {
      const { latestActivity, isCurrentHead, isOverwritten } =
        getRestoreState(messageId);

      const isRestoring = restoringActivityId === latestActivity?.id;
      const canRestore =
        !!latestActivity &&
        !isOverwritten &&
        !isCurrentHead &&
        !restoringActivityId;

      return {
        latestActivity,
        isCurrentHead,
        isOverwritten,
        isRestoring,
        canRestore,
        restoringActivityId,
      };
    },
    [getRestoreState, restoringActivityId],
  );

  return {
    restore,
    getMessageRestoreInfo,
    isAnyRestoreInProgress: !!restoringActivityId,
  };
};
